import { useEffect, useRef } from 'react';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { fetchConfirmation } from '@/lib/booking';
import { applySeo } from '@/lib/seo';
import { BookingDetails } from './booking.$token';
import { type BookingView } from '@/api/generated';
import { captureBookingViewed } from '@/lib/analytics';

// Confirmation page (/confirmation?token=…).
// Reached after a successful PayPal capture. The booking is created
// ASYNCHRONOUSLY (capture → outbox → worker), so we poll GET /confirmation with
// the short-lived signed token returned by capture until the booking row
// exists, then render the full confirmation (reusing BookingDetails). If it
// never materialises within the poll window (or there's no token), we fall back
// to a "payment received — check your email" message (never an error).
//
// Privacy: the token is in the URL → inject no-referrer + noindex (mirror the
// booking-view route) so the token never leaks via Referer or search indexes.
export const Route = createFileRoute('/confirmation')({
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({
    token: typeof s.token === 'string' ? s.token : undefined,
  }),
  component: ConfirmationPage,
});

const MAX_POLLS = 15; // ~30s at 2s intervals
const POLL_MS = 2000;

 
export function ConfirmationPage() {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const pollsRef = useRef(0);

  // No-referrer: keep the token out of any third-party Referer header.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'referrer');
    meta.setAttribute('content', 'no-referrer');
    meta.setAttribute('data-confirmation', '');
    document.head.appendChild(meta);
    return () => {
      document.head.querySelector('meta[data-confirmation]')?.remove();
    };
  }, []);

  useEffect(() => {
    return applySeo({ title: 'Booking confirmed — ollio', noindex: true });
  }, []);

  const query = useQuery({
    queryKey: ['confirmation', token],
    enabled: !!token,
    queryFn: async () => {
      pollsRef.current += 1;
      return fetchConfirmation(token as string);
    },
    // Poll every 2s until the booking arrives or we hit the cap.
    refetchInterval: (q) => (q.state.data || pollsRef.current >= MAX_POLLS ? false : POLL_MS),
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  return <ConfirmationBody query={query} token={token} pollsRef={pollsRef} />;
}

// Split out so the booking.viewed effect below can live beside the data it
// reports on, without adding a hook after an early return.
function ConfirmationBody({
  query,
  token,
  pollsRef,
}: {
  query: UseQueryResult<BookingView | null, Error>;
  token: string | undefined;
  pollsRef: { current: number };
}) {
  // OH-71: the guest reached a confirmed booking straight after paying. This
  // is the funnel's terminal step and was never captured.
  useEffect(() => {
    if (query.data) captureBookingViewed('confirmation');
  }, [query.data]);

  if (query.data) {
    // OH-159: justPaid — this render IS the "just closed the tab after
    // paying" instant the copy needs to address. See BookingDetails' own
    // doc comment on the prop for why /booking/$token (every OTHER visit to
    // a booking) does not pass it.
    return <BookingDetails booking={query.data} justPaid />;
  }

  const exhausted = !token || (pollsRef.current >= MAX_POLLS && !query.data);

  return (
    <main className="mx-auto w-full max-w-xl flex-1 px-6 py-16">
      <div className="rounded-[var(--radius-2xl)] border border-[var(--border-hairline)] bg-card p-8 text-center shadow-[var(--shadow-lg)]">
        {!exhausted ? (
          <div className="mx-auto mb-5 flex h-[60px] w-[60px] items-center justify-center rounded-full border-2 border-[var(--ollio-success)] bg-[rgba(47,125,82,0.22)]">
            <CheckCircle2 className="h-8 w-8 text-[#7fd6a0]" aria-hidden="true" />
          </div>
        ) : null}
        <h1 className="font-serif text-2xl font-semibold tracking-tight text-[var(--ollio-ink)]">
          Payment received
        </h1>
        <p className="mt-2 text-muted">
          {exhausted
            ? // OH-159: the booking never showed up within the poll window (or
              // there was no token at all) — the guest's only way to the
              // booking is now whatever email is coming. We don't have an
              // address to name here (the booking row that would carry
              // guest_email never loaded), so this says what the email is
              // FOR rather than guessing who it went to.
              "We're finalizing your booking — a confirmation with a link back to it is on its way to your inbox. If you close this tab, that email is how you get back to your booking."
            : "We're finalizing your booking now…"}
        </p>
      </div>
    </main>
  );
}
